import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { X, Loader2, Plus, Trash2, Pencil, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { RULE_FIELDS, RULE_OPS, RULE_PLACEHOLDERS, ruleFilterMatch } from '@/lib/tagRules';
import { tagThemeClass } from './tagTheme';
import { TagRulePreview } from './TagRulePreview';
import { PlaceholderInput } from './PlaceholderInput';
import { TagClassOrder } from './TagClassOrder';

const INPUT = 'h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';
const BASE = '/notes/tag-workspace/admin/tag-rules';
const COND = { field: 'code', op: 'eq', value: '', value2: '' };
const EMPTY = { name: '', views: 'expanded', active: true, conditions: [COND], actions: { note: '', rename: '', merge: true, hide: false, hide_pieces: false, stop: false } };
const VIEW = { expanded: 'Expanded only', collapsed: 'Collapsed only', both: 'Both views' };
const MATS = [['diamond', 'Diamond'], ['color_stone', 'Colour Stone'], ['pearl', 'Pearl']];
const fLabel = (id) => RULE_FIELDS.find((x) => x.id === id)?.label || id;
const oDef = (id) => RULE_OPS.find((x) => x.id === id) || RULE_OPS[0];

function describe(r) {
  const c = (r.conditions || []).map((x) => `${fLabel(x.field)} ${oDef(x.op).label.split(' (')[0]}${oDef(x.op).noValue ? '' : ` ${x.value}${x.op === 'between' ? ` – ${x.value2}` : ''}`}`).join(' AND ');
  const a = r.actions || {};
  const t = [a.rename && `rename to "${a.rename}"`, a.note && `note "${a.note}"`, a.merge && 'merge', a.hide && 'hide line', a.hide_pieces && 'hide pieces', a.stop && 'stop later rules'].filter(Boolean).join(', ');
  return { c, t };
}

function ValueInput({ cond, onChange, testid }) {
  if (cond.field === 'material_type' && ['eq', 'neq'].includes(cond.op)) {
    return (
      <select value={cond.value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} flex-1`} data-testid={testid}>
        <option value="">Choose…</option>
        {MATS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    );
  }
  const list = ['code', 'display_name'].includes(cond.field) ? `tagws-rule-${cond.field}` : undefined;
  return <input list={list} value={cond.value} onChange={(e) => onChange(e.target.value)} placeholder="Value" className={`${INPUT} min-w-0 flex-1`} data-testid={testid} />;
}

function ConditionRow({ cond, onChange, onRemove, idx }) {
  const op = oDef(cond.op);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="tagws-rule-condition">
      <span className="w-10 text-xs font-semibold text-muted-foreground">{idx ? 'AND' : 'IF'}</span>
      <select value={cond.field} onChange={(e) => onChange({ field: e.target.value, value: '' })} className={INPUT} data-testid={`tagws-rule-cond-field-${idx}`}>
        {RULE_FIELDS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
      </select>
      <select value={cond.op} onChange={(e) => onChange({ op: e.target.value })} className={INPUT} data-testid={`tagws-rule-cond-op-${idx}`}>
        {RULE_OPS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      {op.noValue ? null : <ValueInput cond={cond} onChange={(v) => onChange({ value: v })} testid={`tagws-rule-cond-value-${idx}`} />}
      {op.two ? <input value={cond.value2} onChange={(e) => onChange({ value2: e.target.value })} placeholder="and below" className={`${INPUT} w-28`} data-testid={`tagws-rule-cond-value2-${idx}`} /> : null}
      <button type="button" onClick={onRemove} aria-label="Remove condition" className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:text-destructive" data-testid={`tagws-rule-cond-remove-${idx}`}><X size={14} /></button>
    </div>
  );
}

function ActionsEditor({ a, set }) {
  const box = (k, label) => (
    <label className="flex items-center gap-1.5 text-sm text-foreground">
      <input type="checkbox" checked={!!a[k]} onChange={(e) => set({ [k]: e.target.checked })} data-testid={`tagws-rule-action-${k}`} /> {label}
    </label>
  );
  return (
    <div className="space-y-2">
      <div className="grid gap-2 md:grid-cols-2">
        <PlaceholderInput value={a.note} maxLength={60} onChange={(v) => set({ note: v })} placeholder="Note — blank shows no note; {note} = portal note" className={INPUT} testid="tagws-rule-action-note" />
        <PlaceholderInput value={a.rename} maxLength={60} onChange={(v) => set({ rename: v })} placeholder="Rename line to (e.g. {display_name} {shape})" className={INPUT} testid="tagws-rule-action-rename" />
      </div>
      <div className="flex flex-wrap gap-4">{box('merge', 'Merge matching lines (add ct + pcs)')}{box('hide', 'Hide line')}{box('hide_pieces', 'Hide pieces')}{box('stop', "Stop: don't apply later rules to this line")}</div>
      <p className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">Note box left blank = <b>no note</b> on the tag for matching lines; use <b>{'{note}'}</b> to keep the portal note. Type <b>{'{'}</b> to insert a placeholder: {RULE_PLACEHOLDERS.map((p) => `{${p.id}}`).join(' ')}</p>
    </div>
  );
}

function RuleEditor({ f, setF, editing, onSubmit, onCancel, classes }) {
  const setCond = (i, p) => setF({ ...f, conditions: f.conditions.map((c, j) => (j === i ? { ...c, ...p } : c)) });
  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-border bg-secondary/40 p-3" data-testid="tagws-rule-form">
      <div className="flex flex-wrap gap-2">
        <input value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Rule name (optional)" className={`${INPUT} min-w-[200px] flex-1`} data-testid="tagws-rule-name" />
        <select value={f.views} onChange={(e) => setF({ ...f, views: e.target.value })} className={INPUT} data-testid="tagws-rule-views">
          {Object.entries(VIEW).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        {f.conditions.map((c, i) => <ConditionRow key={i} idx={i} cond={c} onChange={(p) => setCond(i, p)} onRemove={() => setF({ ...f, conditions: f.conditions.filter((_, j) => j !== i) })} />)}
        <button type="button" onClick={() => setF({ ...f, conditions: [...f.conditions, COND] })} className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--ring))]" data-testid="tagws-rule-add-condition"><Plus size={13} /> Add condition</button>
      </div>
      <div className="border-t border-border pt-3"><span className="mb-2 block text-xs font-semibold text-muted-foreground">THEN</span><ActionsEditor a={f.actions} set={(p) => setF({ ...f, actions: { ...f.actions, ...p } })} /></div>
      <datalist id="tagws-rule-code">{classes.map((c) => <option key={`c${c.material_type}${c.code}`} value={c.code} />)}</datalist>
      <datalist id="tagws-rule-display_name">{classes.filter((c) => c.tag_display_name).map((c) => <option key={`d${c.material_type}${c.code}`} value={c.tag_display_name} />)}</datalist>
      <div className="flex justify-end gap-2">
        {editing ? <button type="button" onClick={onCancel} className="h-9 rounded-lg border border-border px-3 text-sm" data-testid="tagws-rule-cancel">Cancel</button> : null}
        <button type="submit" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90" data-testid="tagws-rule-save">{editing ? 'Save rule' : <><Plus size={15} /> Add rule</>}</button>
      </div>
    </form>
  );
}

function RuleRow({ r, i, pos, n, onEdit, onMove, onChanged, count, onShow, drag, allTag }) {
  const { c, t } = describe(r);
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(extractError(e, 'Could not update the rule.')); } };
  const toggle = (v) => call(() => notesApi.put(`${BASE}/${r.id}`, { ...r, active: v }), v ? 'Rule on.' : 'Rule off.');
  const remove = () => { if (window.confirm(`Delete this rule${r.name ? ` "${r.name}"` : ''}?`)) call(() => notesApi.delete(`${BASE}/${r.id}`), 'Rule deleted.'); };
  const btn = 'inline-grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-30';
  return (
    <tr draggable onDragStart={() => drag.start(i)} onDragOver={(e) => { e.preventDefault(); drag.over(i); }} onDrop={(e) => { e.preventDefault(); drag.drop(i); }} onDragEnd={drag.end}
      className={`border-t border-border align-top ${drag.target === i ? 'bg-[hsl(var(--accent))]' : ''}`} data-testid="tagws-rule-row">
      <td className="cursor-grab px-2 py-2 text-xs text-muted-foreground active:cursor-grabbing" title="Drag to reorder"><span className="inline-flex items-center gap-0.5"><GripVertical size={13} data-testid="tagws-rule-drag-handle" />{pos + 1}</span></td>
      <td className="px-3 py-2"><div className="font-medium text-foreground">{r.name || 'Untitled rule'}{allTag ? <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[length:calc(var(--fs,1)*0.625rem)] font-normal text-muted-foreground" data-testid="tagws-rule-all-tag">{allTag}</span> : null}</div><div className="text-xs text-muted-foreground" data-testid="tagws-rule-row-summary"><b>IF</b> {c} <b>THEN</b> {t}</div></td>
      <td className="px-3 py-2 text-sm">{VIEW[r.views]}</td>
      <td className="px-3 py-2 text-center">
        {r.active ? (
          <button type="button" onClick={() => onShow(r.id)} disabled={!count} title={count ? 'Show these pieces' : 'No pieces changed'}
            className="min-w-[2.25rem] rounded-full border border-border px-2 py-0.5 font-mono text-xs text-foreground hover:border-[hsl(var(--ring))] hover:bg-[hsl(var(--accent))] disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
            data-testid="tagws-rule-usage-button">{count || 0}</button>
        ) : <span className="text-xs text-muted-foreground" data-testid="tagws-rule-usage-inactive">—</span>}
      </td>
      <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!r.active} onChange={(e) => toggle(e.target.checked)} data-testid="tagws-rule-active-toggle" /></td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <button disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Move up" className={btn} data-testid="tagws-rule-up-button"><ArrowUp size={14} /></button>
        <button disabled={i === n - 1} onClick={() => onMove(i, 1)} aria-label="Move down" className={btn} data-testid="tagws-rule-down-button"><ArrowDown size={14} /></button>
        <button onClick={() => onEdit(r)} aria-label="Edit" className={btn} data-testid="tagws-rule-edit-button"><Pencil size={14} /></button>
        <button onClick={remove} aria-label="Delete" className={`${btn} hover:text-destructive`} data-testid="tagws-rule-delete-button"><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}

function RuleFilterBar({ filt, setFilt, classes, shown, total }) {
  const list = filt.field === 'code' ? 'tagws-rule-code' : filt.field === 'display_name' ? 'tagws-rule-display_name' : undefined;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="tagws-rule-filter-bar">
      <span className="font-semibold">Filter rules by</span>
      <select value={filt.field} onChange={(e) => setFilt({ field: e.target.value, value: '' })} className={`${INPUT} h-8`} data-testid="tagws-rule-filter-field">
        <option value="">All rules</option>
        {RULE_FIELDS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
      </select>
      {filt.field ? (
        filt.field === 'material_type' ? (
          <select value={filt.value} onChange={(e) => setFilt({ ...filt, value: e.target.value })} className={`${INPUT} h-8`} data-testid="tagws-rule-filter-value">
            <option value="">Any value</option>
            {MATS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ) : (
          <input list={list} value={filt.value} onChange={(e) => setFilt({ ...filt, value: e.target.value })} placeholder="Value (e.g. POLKI or 2.00)" className={`${INPUT} h-8 w-56`} data-testid="tagws-rule-filter-value" />
        )
      ) : null}
      {filt.field ? <button type="button" onClick={() => setFilt({ field: '', value: '' })} className="text-[hsl(var(--ring))]" data-testid="tagws-rule-filter-clear">Clear</button> : null}
      <span className="ml-auto" data-testid="tagws-rule-filter-count">{shown} of {total} rules{filt.field ? ' · drag to reorder within this list' : ''}</span>
    </div>
  );
}

// Super admin console: IF → THEN rules applied top to bottom to every stone line on a tag.
export function TagRulesAdmin({ open, onClose, onChanged, classifications = [], items = [], defaultId, usage = {}, onShowRule }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await notesApi.get(BASE); setList(data || []); }
    catch (e) { toast.error(extractError(e, 'Could not load tag rules.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const changed = () => { load(); onChanged?.(); };
  const reset = () => { setF(EMPTY); setEditId(null); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await notesApi.put(`${BASE}/${editId}`, f); else await notesApi.post(BASE, f);
      toast.success(editId ? 'Rule saved.' : 'Rule added.'); reset(); changed();
    } catch (err) { toast.error(extractError(err, 'Could not save the rule.')); }
  };
  const dragFrom = useRef(null);
  const [dragTo, setDragTo] = useState(null);
  const [filt, setFilt] = useState({ field: '', value: '' });
  const shown = list.map((r, pos) => ({ r, pos, m: ruleFilterMatch(r, filt.field, filt.value) })).filter((x) => x.m);
  // Reorder among the visible rules only; hidden rules keep their slots.
  const reorder = async (from, to) => {
    if (from == null || to == null || from === to) return;
    const slots = shown.map((x) => x.pos);
    const vis = shown.map((x) => x.r.id);
    const [m] = vis.splice(from, 1);
    vis.splice(to, 0, m);
    const ids = list.map((r) => r.id);
    slots.forEach((slot, k) => { ids[slot] = vis[k]; });
    setList((l) => ids.map((rid) => l.find((r) => r.id === rid)));
    try { await notesApi.put(`${BASE}/reorder`, { ids }); changed(); } catch (e) { toast.error(extractError(e, 'Could not reorder.')); load(); }
  };
  const drag = {
    target: dragTo,
    start: (i) => { dragFrom.current = i; },
    over: (i) => setDragTo(i),
    drop: (i) => { reorder(dragFrom.current, i); dragFrom.current = null; setDragTo(null); },
    end: () => { dragFrom.current = null; setDragTo(null); },
  };
  const move = (i, d) => reorder(i, i + d);
  if (!open) return null;
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-tag-rules-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">Tag rules</h3>
            <p className="text-xs text-muted-foreground">Every matching rule runs on each stone line, top to bottom, on all tags (printed or scanned). Number checks use the largest number in the text (e.g. sieve “+2-6.1/2” = 6.5). Drag rows or use the arrows to change the order — lines on the tag also follow this order within each classification.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="tagws-tag-rules-close-button" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <RuleEditor f={f} setF={setF} editing={!!editId} onSubmit={submit} onCancel={reset} classes={classifications} />
        <div className="mt-3"><TagRulePreview items={items} defaultId={defaultId} saved={list} draft={f} editId={editId} /></div>
        <TagClassOrder classes={classifications} />
        <RuleFilterBar filt={filt} setFilt={setFilt} classes={classifications} shown={shown.length} total={list.length} />
        <div className="mt-2 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="tagws-tag-rules-table">
            <thead className="bg-secondary/60 text-left text-[length:calc(var(--fs,1)*0.6875rem)] uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Rule</th><th className="px-3 py-2">View</th><th className="px-3 py-2 text-center">Pieces</th><th className="px-3 py-2 text-center">Active</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center"><Loader2 size={16} className="mx-auto animate-spin text-muted-foreground" /></td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="tagws-tag-rules-empty">No rules yet. Lines show their note, or their sieve size if there is no note.</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="tagws-tag-rules-filter-empty">No rules match this filter.</td></tr>
              ) : shown.map(({ r, pos, m }, i) => <RuleRow key={r.id} r={r} i={i} pos={pos} n={shown.length} allTag={m === 'all' ? (['code', 'display_name'].includes(filt.field) ? 'all classifications' : `any ${(RULE_FIELDS.find((f) => f.id === filt.field)?.label || '').toLowerCase()}`) : ''} onEdit={(x) => { setEditId(x.id); setF({ ...EMPTY, ...x, actions: { ...EMPTY.actions, ...x.actions } }); }} onMove={move} onChanged={changed} count={usage[r.id]} onShow={(rid) => onShowRule?.(rid)} drag={drag} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
